/**
 * Driver Home Screen - 2D Dashboard
 * Jamaican-themed with Green, Yellow, Black branding
 */
import { useState, useEffect, useCallback, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DriverOpsPanel from '../components/DriverOpsPanel';
import DriverRidePanel from '../components/DriverRidePanel';
import QRScanner from '../components/QRScanner';
import ImageCropper from '../components/ImageCropper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Error Boundary to catch rendering errors
class DriverHomeErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('DriverHome Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#E8FFF4' }}>
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4 text-center">{this.state.error?.message || 'An unexpected error occurred'}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import api from '../lib/api';
import BankDetailsForm from '../components/BankDetailsForm';
import PushNotificationBanner from '../components/PushNotificationBanner';
import { 
  Bell, 
  User,
  Power,
  MapPin,
  Navigation,
  QrCode,
  Clock,
  DollarSign,
  Package,
  Star,
  TrendingUp,
  Home,
  Briefcase,
  Wallet,
  ChevronRight,
  Timer,
  Truck,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Shield,
  Award,
  Camera,
  X,
  Phone,
  Mail,
  Car,
  Save,
  Loader2,
  Building2, Volume2, Settings
} from 'lucide-react';

// Jamaican Color Palette

// Jamaica parishes list
const JAMAICA_PARISHES = [
  "Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester",
  "St. Elizabeth", "Westmoreland", "Hanover", "St. James", "Trelawny",
  "St. Ann", "St. Mary", "Portland", "St. Thomas"
];

// Notification sounds options
const NOTIFICATION_SOUNDS = [
  { id: "chain", name: "Chain", desc: "Classic chain link sound" },
  { id: "bell", name: "Bell", desc: "Simple bell tone" },
  { id: "chime", name: "Chime", desc: "Gentle chime" },
  { id: "ping", name: "Ping", desc: "Quick ping" },
  { id: "horn", name: "Horn", desc: "Truck horn" }
];

const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
  white: '#FFFFFF',
  paleGreen: '#E8FFF4',
};

function Fallback2D() {
  return (
    <div className="relative h-full flex items-center justify-center overflow-hidden">
      <div className="absolute w-64 h-64 rounded-full bg-gradient-to-br from-[#00A86B]/10 to-[#FFD300]/10 animate-pulse" />
      
      {/* Box */}
      <div className="relative w-28 h-24 bg-white rounded-xl shadow-2xl border-2 border-[#00A86B] transform -rotate-6 hover:rotate-0 transition-transform">
        <div className="absolute top-0 left-0 right-0 h-3 bg-[#FFD300] rounded-t-xl" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-[#00A86B] flex items-center justify-center">
            <div className="flex -space-x-1">
              <div className="w-3 h-3 border-2 border-white rounded-full" />
              <div className="w-3 h-3 border-2 border-white rounded-full" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Route dots */}
      <div className="absolute bottom-16 left-8 right-8 flex items-center justify-between">
        <div className="w-4 h-4 bg-[#00A86B] rounded-full animate-pulse shadow-lg" />
        <div className="flex-1 mx-2 border-b-2 border-dashed border-[#FFD300]" />
        <div className="w-4 h-4 bg-black rounded-full animate-pulse shadow-lg" />
      </div>
      
      {/* Chain links */}
      <div className="absolute left-4 bottom-8 flex">
        {['#00A86B', '#FFD300', '#000', '#00A86B'].map((c, i) => (
          <div key={i} className="w-4 h-4 border-3 rounded-full -ml-1 first:ml-0" style={{ borderColor: c, borderWidth: '3px' }} />
        ))}
      </div>
    </div>
  );
}

// ============== COUNTDOWN TIMER ==============
function CountdownTimer({ deadline, onExpire }) {
  const [timeLeft, setTimeLeft] = useState(null);
  
  useEffect(() => {
    const calculateTime = () => {
      const now = new Date().getTime();
      const end = new Date(deadline).getTime();
      const diff = end - now;
      
      if (diff <= 0) {
        onExpire && onExpire();
        return { minutes: 0, seconds: 0, expired: true };
      }
      
      return {
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        expired: false
      };
    };
    
    setTimeLeft(calculateTime());
    const interval = setInterval(() => setTimeLeft(calculateTime()), 1000);
    return () => clearInterval(interval);
  }, [deadline, onExpire]);
  
  if (!timeLeft) return null;
  
  const isUrgent = timeLeft.minutes < 10;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold ${
      timeLeft.expired ? 'bg-red-100 text-red-600' :
      isUrgent ? 'bg-amber-100 text-amber-600' :
      'bg-emerald-100 text-emerald-600'
    }`}>
      <Timer className="h-4 w-4" />
      {timeLeft.expired ? 'EXPIRED' : 
        `${String(timeLeft.minutes).padStart(2, '0')}:${String(timeLeft.seconds).padStart(2, '0')}`
      }
    </div>
  );
}

// ============== MAIN COMPONENT ==============
const DriverHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [driver, setDriver] = useState(null);
  const [jobs, setJobs] = useState({ assigned: [], available: [] });
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scanningOrderId, setScanningOrderId] = useState(null);
  const [earnings, setEarnings] = useState({ today: 0, tips: 0, balance: 0, deliveries: 0 });
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  
  const [urgentOffers, setUrgentOffers] = useState([]);
  const [acceptingOffer, setAcceptingOffer] = useState(null);
  // 3D removed for stability - using 2D fallback
  const [useOpsPanel, setUseOpsPanel] = useState(true); // Feature flag for new driver ops panel
  const [activeTab, setActiveTab] = useState('home');
  
  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [preferences, setPreferences] = useState({
    preferred_parishes: [],
    accepts_bulky: false,
    accepts_fragile: true,
    accepts_food: true,
    notification_sound: "chain"
  });
  const [playingSound, setPlayingSound] = useState(null);
  const [showBanner, setShowBanner] = useState(true); // 180-day promo banner
  const [prefLoading, setPrefLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  
  // Handle cropped profile photo upload
  const handleCroppedPhoto = useCallback(async (croppedBlob) => {
    try {
      setProfileLoading(true);
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');
      const response = await api.post('/drivers/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.photo_url) {
        setProfileData(prev => ({ ...prev, photo_url: response.data.photo_url }));
        toast.success('Profile photo updated!');
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      toast.error('Failed to upload photo');
    } finally {
      setShowCropper(false);
      setPendingPhotoFile(null);
      setProfileLoading(false);
    }
  }, []);
  const [profileData, setProfileData] = useState({
    photo_url: '',
    display_name: '',
    phone: '',
    vehicle_type: '',
    vehicle_plate: '',
    bio: ''
  });
  
  // Jamaican greeting - "Waah Gwaan?" = "What's going on?"
  const getGreeting = () => {
    return 'Waah Gwaan?';
  };
  
  // Fetch driver data
  const fetchDriverData = useCallback(async () => {
    try {
      const [driverRes, jobsRes, earningsRes, prefsRes] = await Promise.all([
        api.get('/drivers/me').catch(() => null),
        api.get('/drivers/jobs').catch(() => ({ data: { assigned: [], available: [] } })),
        api.get('/drivers/earnings').catch(() => ({ data: {} })),
        api.get('/drivers/preferences').catch(() => ({ data: { preferences: {} } }))
      ]);
      
      if (driverRes?.data) setDriver(driverRes.data);
      if (jobsRes?.data) setJobs(jobsRes.data);
      if (earningsRes?.data) {
        setEarnings({
          today: earningsRes.data.today_earnings || 0,
          tips: earningsRes.data.today_tips || 0,
          balance: earningsRes.data.current_balance || 0,
          deliveries: earningsRes.data.today_deliveries || driver?.total_deliveries || 0
        });
      }
      if (prefsRes?.data?.preferences) {
        setPreferences(prefsRes.data.preferences);
      }
    } catch (error) {
      console.error('Failed to load driver data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchDriverData();
    
    // Check WebGL
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
      if (!gl) setUse3D(false);
    } catch (e) {
      setUse3D(false);
    }
    
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchDriverData, 30000);
    return () => clearInterval(interval);
  }, [fetchDriverData]);
  
  // Poll for urgent offers
  useEffect(() => {
    const fetchUrgent = async () => {
      try {
        const res = await api.get('/urgent-offers/available');
        if (res?.data?.offers) setUrgentOffers(res.data.offers);
      } catch {}
    };
    fetchUrgent();
    const interval = setInterval(fetchUrgent, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleAcceptOffer = async (offerId) => {
    setAcceptingOffer(offerId);
    try {
      const res = await api.post(`/urgent-offers/${offerId}/accept`);
      if (res.data.success) {
        toast.success(res.data.message);
        setUrgentOffers(prev => prev.filter(o => o.id !== offerId));
        fetchDriverData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setAcceptingOffer(null);
    }
  };

  // Toggle online/offline
  const toggleStatus = async () => {
    if (!driver) return;
    setStatusLoading(true);
    try {
      const newStatus = driver.online_status === 'online' ? 'offline' : 'online';
      const endpoint = newStatus === 'online' ? '/drivers/go-online' : '/drivers/go-offline';
      await api.post(endpoint);
      setDriver({ ...driver, online_status: newStatus });
      toast.success(`You are now ${newStatus === 'online' ? 'Online - Ready for jobs!' : 'Offline'}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  // Profile functions
  const openProfileModal = () => {
    console.log('Opening profile modal, driver:', driver);
    setProfileData({
      photo_url: driver?.photo_url || '',
      display_name: driver?.user_name || user?.name || '',
      phone: driver?.user_phone || user?.phone || '',
      vehicle_type: driver?.vehicle_type || '',
      vehicle_plate: driver?.license_plate || '',
      bio: driver?.bio || ''
    });
    setShowProfileModal(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    // Open image cropper for 1:1 profile photo
    setPendingPhotoFile(file);
    setShowCropper(true);
  };

  const saveProfile = async () => {
    setProfileLoading(true);
    try {
      await api.put('/drivers/profile', {
        display_name: profileData.display_name,
        phone: profileData.phone,
        vehicle_type: profileData.vehicle_type,
        vehicle_plate: profileData.vehicle_plate,
        bio: profileData.bio,
        photo_url: profileData.photo_url
      });
      setDriver({ ...driver, ...profileData });
      toast.success('Profile updated successfully!');
      setShowProfileModal(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };
  
  
  // Preference functions
  const toggleParish = (parish) => {
    const current = preferences.preferred_parishes || [];
    if (current.includes(parish)) {
      setPreferences({...preferences, preferred_parishes: current.filter(p => p !== parish)});
    } else {
      setPreferences({...preferences, preferred_parishes: [...current, parish]});
    }
  };

  const selectSound = async (soundId) => {
    setPlayingSound(soundId);
    try {
      const audio = new Audio(`/sounds/${soundId}.wav`);
      await audio.play();
      setPreferences({...preferences, notification_sound: soundId});
      setTimeout(() => setPlayingSound(null), 2000);
    } catch (e) {
      console.error("Sound playback failed:", e);
      setPlayingSound(null);
    }
  };

  const handleSavePreferences = async () => {
    setPrefLoading(true);
    try {
      await api.put("/drivers/preferences", preferences);
      toast.success("Preferences saved!");
      setShowPreferencesModal(false);
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setPrefLoading(false);
    }
  };

// Navigation handlers
  // Handle QR scan success - complete delivery
  const handleScanSuccess = async (qrData) => {
    try {
      await api.post(`/drivers/deliveries/${scanningOrderId}/complete`, {
        qr_code: qrData,
        completed_at: new Date().toISOString()
      });
      toast.success("Delivery completed successfully!");
      setShowQRScanner(false);
      setScanningOrderId(null);
      fetchDriverData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete delivery");
    }
  };

  const openNavigation = (location) => {
    if (!location) return;
    const address = `${location.street || ''}, ${location.city || ''}, Jamaica`;
    const encoded = encodeURIComponent(address);
    
    // Try Google Maps first
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };
  
  // If loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #E8FFF4 100%)' }}>
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // If user is suspended - show clear message
  if (user?.suspended) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FEE2E2 100%)' }}>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <AlertCircle className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Account Suspended</h2>
          <p className="text-gray-600 mb-4 max-w-sm">
            Your driver account has been temporarily suspended.
          </p>
          {user?.suspension_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-sm">
              <p className="text-sm text-red-700">
                <span className="font-semibold">Reason:</span> {
                  user.suspension_reason === 'low_trust_score_auto' 
                    ? 'Trust score dropped below acceptable threshold due to recent activity'
                    : user.suspension_reason.replace(/_/g, ' ')
                }
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500 mb-6 max-w-sm">
            Please contact support to review your account status and resolve any issues.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => window.location.href = 'mailto:support@liantiao.com?subject=Account%20Suspension%20Review'}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-6"
            >
              Contact Support
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-gray-300 rounded-xl px-6"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // If not registered as driver
  if (!driver) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #E8FFF4 100%)' }}>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Truck className="h-20 w-20 text-[#00A86B] mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Become a Driver</h2>
          <p className="text-gray-600 mb-8 max-w-sm">Join  and start earning by delivering packages across Jamaica</p>
          <Button 
            onClick={() => navigate('/driver/register')}
            className="h-14 px-8 text-lg font-bold rounded-2xl text-white"
            style={{ background: COLORS.green }}
          >
            Register as Driver
          </Button>
        </div>
      </div>
    );
  }
  
  const isOnline = driver.online_status === 'online';
  const hasActiveJobs = jobs.assigned?.length > 0;
  
  return (
    <div className="min-h-screen flex flex-col pb-20" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #E8FFF4 100%)" }}>
      <PushNotificationBanner />
      {/* 180-Day Promo Banner */}
      {showBanner && (
        <div className="bg-gradient-to-r from-[#00A86B] to-[#008B5A] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="text-white font-bold text-sm">180 Days of Excellence!</p>
              <p className="text-white/80 text-xs">Save your bank details now for faster payouts</p>
            </div>
          </div>
          <button onClick={() => setShowBanner(false)} className="text-white/60 hover:text-white p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Pacifico, cursive" }}>
              <span className="text-[#00A86B]">{getGreeting()}</span>, <span className="text-gray-800">{user?.name?.split(" ")[0] || "Driver"}</span>
            </h1>
            <p className="text-sm text-gray-500">Ready to deliver?</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Online/Offline Toggle Button */}
            <button
              onClick={toggleStatus}
              disabled={statusLoading}
              className={`px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 flex items-center gap-2 shadow-lg ${
                isOnline 
                  ? 'bg-[#00A86B] text-white shadow-[#00A86B]/30' 
                  : 'bg-gray-800 text-white shadow-gray-800/30'
              }`}
              style={{ boxShadow: isOnline ? '0 4px 20px rgba(0,168,107,0.4)' : '0 4px 20px rgba(0,0,0,0.2)' }}
            >
              <Power className={`h-4 w-4 ${statusLoading ? 'animate-spin' : ''}`} />
              {isOnline ? 'Online' : 'Go Online'}
            </button>
            <button className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors relative">
              <Bell className="h-5 w-5 text-gray-600" />
              {hasActiveJobs && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />}
            </button>
            <button 
              onClick={openProfileModal}
              className="w-10 h-10 bg-gradient-to-br from-[#00A86B] to-[#008B5A] rounded-full flex items-center justify-center overflow-hidden"
            >
              {driver?.photo_url ? (
                <img src={driver.photo_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
        </div>
      </header>
      
      {/* Driver Ops Panel or 3D Hero Section */}
      {useOpsPanel ? (
        <div className="mx-4 mt-4">
          <>
            {activeTab === "rides" ? (
              <DriverRidePanel onRideUpdate={fetchDriverData} />
            ) : (
              <DriverOpsPanel onJobUpdate={fetchDriverData} />
            )}
          </>
        </div>
      ) : (
      <div className="mx-4 mt-4 rounded-3xl overflow-hidden shadow-xl" style={{ 
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FFF8 100%)',
        border: '1px solid rgba(0,168,107,0.2)'
      }}>
        {/* 3D Canvas */}
        <div className="h-48 relative">
          <Fallback2D />
        </div>
        
        {/* Hero Content */}
        <div className="px-5 pb-5 -mt-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Driver Dashboard</h2>
              <p className="text-xs text-gray-500">Deliver packages, earn more, track progress</p>
            </div>
            
            {/* Online/Offline Toggle */}
            <button
              onClick={toggleStatus}
              disabled={statusLoading}
              className={`px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 flex items-center gap-2 shadow-lg ${
                isOnline 
                  ? 'bg-[#00A86B] text-white shadow-[#00A86B]/30' 
                  : 'bg-gray-800 text-white shadow-gray-800/30'
              }`}
              style={{ boxShadow: isOnline ? '0 4px 20px rgba(0,168,107,0.4)' : '0 4px 20px rgba(0,0,0,0.2)' }}
            >
              <Power className={`h-4 w-4 ${statusLoading ? 'animate-spin' : ''}`} />
              {isOnline ? 'Online' : 'Go Online'}
            </button>
          </div>
        </div>
      </div>
      
      )}
      
      {/* URGENT OFFERS BANNER */}
      {urgentOffers.length > 0 && (
        <div className="px-4 mt-4 space-y-3" data-testid="urgent-offers-section">
          {urgentOffers.map(offer => (
            <div 
              key={offer.id}
              className={`relative overflow-hidden rounded-2xl border-2 shadow-lg ${
                offer.is_hot 
                  ? 'border-orange-400 bg-gradient-to-r from-orange-50 to-red-50' 
                  : 'border-red-400 bg-gradient-to-r from-red-50 to-orange-50'
              }`}
              style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
              data-testid={`urgent-offer-${offer.id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-3 py-0.5 rounded-full text-xs font-black text-white ${offer.is_hot ? 'bg-orange-500' : 'bg-red-500'}`}>
                        HIGH PRIORITY
                      </span>
                      <span className={`font-black text-base ${offer.is_hot ? 'text-orange-600' : 'text-red-600'}`}>
                        {offer.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-green-600" />
                        <span>{offer.pickup_address?.street || offer.pickup_address?.parish || 'Pickup'}</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span>{offer.dropoff_address?.street || offer.dropoff_address?.parish || 'Dropoff'}</span>
                      </div>
                      <div className="text-gray-500">
                        Size: {offer.package_size} | From: {offer.sender_name}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1.5">
                    <div className={`text-2xl font-black ${offer.is_hot ? 'text-orange-600' : 'text-red-600'}`} data-testid="urgent-offer-amount">
                      J${(offer.driver_fee || offer.driver_earnings_estimate || 0).toLocaleString()}
                    </div>
                    <span className="text-[10px] text-gray-500">Your earnings</span>
                    <button
                      onClick={() => handleAcceptOffer(offer.id)}
                      disabled={acceptingOffer === offer.id}
                      className={`px-4 py-2 rounded-full font-bold text-sm text-white transition-all ${
                        offer.is_hot ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'
                      } ${acceptingOffer === offer.id ? 'opacity-50' : 'shadow-lg'}`}
                      data-testid={`accept-offer-${offer.id}`}
                    >
                      {acceptingOffer === offer.id ? 'Accepting...' : 'Accept Offer'}
                    </button>
                    <span className="text-[10px] text-gray-400">25 min pickup window</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pending Pickups Section - Accepted but not collected */}
      {jobs.assigned?.filter(j => ["accepted", "pending"].includes(j.status))?.length > 0 && (
        <div className="px-4 mt-5">
          <h3 className="text-sm font-bold text-amber-700 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending Pickups ({jobs.assigned?.filter(j => ["accepted", "pending"].includes(j.status))?.length || 0})
          </h3>
          <div className="space-y-3">
            {jobs.assigned?.filter(j => ["accepted", "pending"].includes(j.status)).map((job, idx) => (
              <div 
                key={job.id}
                className="bg-amber-50 rounded-2xl p-4 shadow-sm border border-amber-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                    Awaiting Pickup
                  </span>
                  {job.pickup_deadline && (
                    <CountdownTimer 
                      deadline={job.pickup_deadline}
                      onExpire={() => toast.error('Pickup time expired!')}
                    />
                  )}
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-amber-500 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-800">{job.sender_name || 'Sender'}</p>
                    <p className="text-xs text-gray-600">{job.from_location?.street || job.from_location?.city}</p>
                    {job.sender_phone && (
                      <a href={`tel:${job.sender_phone}`} className="text-xs text-amber-700 font-medium">
                        {job.sender_phone}
                      </a>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => openNavigation(job.from_location)}
                  className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  Navigate to Pickup
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Jobs Section */}
      <div className="px-4 mt-5">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          <Package className="h-4 w-4 text-[#00A86B]" />
          Active Deliveries ({jobs.assigned?.filter(j => ["started", "picked_up", "in_transit"].includes(j.status))?.length || 0})
        </h3>
        
        {!hasActiveJobs ? (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
              <Package className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-gray-600 font-medium mb-1">No active deliveries yet</p>
            <p className="text-xs text-gray-400 mb-4">
              {isOnline ? 'Waiting for new jobs...' : 'Go online to start receiving jobs'}
            </p>
            {!isOnline && (
              <Button
                onClick={toggleStatus}
                className="text-white rounded-xl"
                style={{ background: COLORS.green }}
              >
                Go Online to Start
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.assigned.map((job, idx) => (
              <div 
                key={job.id}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                style={{ borderLeft: `4px solid ${COLORS.green}` }}
              >
                {/* Package Info Header - Tracking & ID for verification */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      {job.tracking_code && (
                        <span className="text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                          #{job.tracking_code}
                        </span>
                      )}
                      <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                        {job.package_id || job.id?.slice(0,8)}
                      </span>
                    </div>
                    {job.created_at && (
                      <span className="text-xs text-gray-400">
                        {new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(job.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {job.distance && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{job.distance} km</span>}
                    {job.eta_minutes && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">ETA {job.eta_minutes} min</span>}
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    job.status === 'accepted' || job.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    job.status === 'started' || job.status === 'picked_up' ? 'bg-blue-100 text-blue-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {job.status === 'accepted' || job.status === 'pending' ? 'Pick Up Package' :
                     job.status === 'started' || job.status === 'picked_up' ? 'On The Way' :
                     `Chain Leg ${job.sequence_number || idx + 1}`}
                  </span>
                  
                  {job.pickup_deadline && (
                    <CountdownTimer 
                      deadline={job.pickup_deadline}
                      onExpire={() => toast.error('Pickup time expired!')}
                    />
                  )}
                </div>
                
                {/* Sender Details with Photo */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-3">
                    {job.sender_photo_url ? (
                      <img src={job.sender_photo_url} alt="Sender" className="w-10 h-10 rounded-full object-cover border-2 border-[#00A86B]" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#00A86B]/20 flex items-center justify-center text-[#00A86B] font-bold">
                        {(job.sender_name || 'S')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-xs text-gray-400">PICKUP FROM</p>
                      <p className="text-sm font-bold text-gray-800">{job.sender_name || 'Sender'}</p>
                      <p className="text-xs text-gray-600">{job.from_location?.street || job.from_location?.city || 'Pickup Location'}</p>
                      {job.sender_phone && (
                        <a href={`tel:${job.sender_phone}`} className="text-xs text-[#00A86B] font-medium flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" /> {job.sender_phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    {job.receiver_photo_url ? (
                      <img src={job.receiver_photo_url} alt="Receiver" className="w-10 h-10 rounded-full object-cover border-2 border-black" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold">
                        {(job.recipient_name || 'R')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-xs text-gray-400">DELIVER TO</p>
                      <p className="text-sm font-bold text-gray-800">{job.recipient_name || 'Receiver'}</p>
                      <p className="text-xs text-gray-600">{job.to_location?.street || job.to_location?.city || 'Dropoff Location'}</p>
                      {job.recipient_phone && (
                        <a href={`tel:${job.recipient_phone}`} className="text-xs text-[#00A86B] font-medium flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" /> {job.recipient_phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => openNavigation(['started','picked_up','in_transit'].includes(job.status) ? job.to_location : job.from_location)}
                    variant="outline"
                    className="flex-1 rounded-xl border-[#00A86B] text-[#00A86B] hover:bg-[#00A86B]/10"
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    Navigate
                  </Button>
                  <Button
                    onClick={() => navigate(`/driver/scan?tab=${(job.status === 'picked_up' || job.status === 'in_transit') ? 'deliver' : 'pickup'}&code=${encodeURIComponent(job.tracking_code || job.package_id || '')}`)}
                    className="flex-1 rounded-xl text-white"
                    style={{ background: job.status === 'picked_up' || job.status === 'in_transit' ? COLORS.green : '#F59E0B' }}
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    {job.status === 'picked_up' || job.status === 'in_transit' ? 'Deliver' : 'Pick Up'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Available Jobs Section */}
      {jobs.available?.length > 0 && (
        <div className="px-4 mt-5">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-[#FFD300]" />
            Available Jobs ({jobs.available.length})
          </h3>
          <div className="space-y-3">
            {jobs.available.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl p-4 shadow-sm border border-amber-200 hover:border-[#00A86B] transition-all">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {job.tracking_code && (
                        <span className="text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                          #{job.tracking_code}
                        </span>
                      )}
                      <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{job.package_id || job.id?.slice(0,8)}</span>
                      {job.created_at && <span className="text-xs text-gray-400">{new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(job.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>}
                    </div>
                    <p className="text-lg font-bold text-[#00A86B]">J${(job.driver_fee || job.driver_earnings_estimate || 0).toLocaleString()}</p>
                  </div>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">{job.distance || '~5'} km</span>
                </div>
                {/* Sender & Receiver Info */}
                <div className="grid grid-cols-2 gap-2 mb-3 p-2 bg-gray-50 rounded-lg text-xs">
                  <div>
                    <p className="text-gray-500">Sender</p>
                    <p className="font-medium text-gray-800 truncate">{job.sender_name || 'N/A'}</p>
                    <p className="text-gray-600">{job.sender_phone || ''}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Receiver</p>
                    <p className="font-medium text-gray-800 truncate">{job.receiver_name || job.recipient_name || 'N/A'}</p>
                    <p className="text-gray-600">{job.receiver_phone || job.recipient_phone || ''}</p>
                  </div>
                </div>
                {/* Pickup & Dropoff Addresses */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#00A86B]" />
                    <span className="text-sm text-gray-700 truncate">{job.pickup_address || job.from_location?.street || job.from_location?.city || 'Pickup'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Navigation className="h-4 w-4 text-[#FFD300]" />
                    <span className="text-sm text-gray-700 truncate">{job.dropoff_address || job.to_location?.street || job.to_location?.city || 'Drop-off'}</span>
                  </div>
                </div>
                <Button onClick={async () => { 
                  try { 
                    await api.post(`/drivers/jobs/${job.id}/accept`); 
                    toast.success('Job accepted!'); 
                    fetchDriverData(); 
                  } catch (e) { 
                    const msg = e.response?.data?.error?.message || e.response?.data?.message || 'Failed to accept job';
                    toast.error(msg);
                  }
                }} className="w-full rounded-xl text-white" style={{background: '#00A86B'}}>Accept Job</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Earnings Summary */}
      <div className="px-4 mt-5">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#00A86B]" />
          Today's Earnings
        </h3>
        
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800">{earnings.deliveries}</p>
              <p className="text-xs text-gray-500">Deliveries</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-[#00A86B]">J${earnings.today.toFixed(0)}</p>
              <p className="text-xs text-gray-500">Earned</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-[#FFD300]">J${earnings.tips.toFixed(0)}</p>
              <p className="text-xs text-gray-500">Tips</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800">J${earnings.balance.toFixed(0)}</p>
              <p className="text-xs text-gray-500">Balance</p>
            </div>
          </div>
          
          <Button
            onClick={() => navigate('/driver/earnings')}
            variant="outline"
            className="w-full mt-4 rounded-xl border-gray-200 text-gray-600"
          >
            View Details
            <ChevronRight className="h-4 w-4 ml-auto" />
          </Button>
        </div>
      </div>
      
      {/* Verification Prompt */}
      {driver && !driver.verified && (
        <div className="px-4 mt-5">
          <div 
            onClick={() => navigate('/driver/vehicle-photos')}
            className="bg-gradient-to-r from-[#00A86B]/10 to-[#FFD300]/10 rounded-2xl p-4 border border-[#00A86B]/30 cursor-pointer hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#00A86B]/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#00A86B]" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-800 flex items-center gap-2">
                  Verify Your Identity
                  <Award className="w-4 h-4 text-[#FFD300]" />
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  Get verified to unlock premium jobs & higher earnings
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      )}
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 py-3 shadow-lg">
        <div className="flex items-center justify-around">
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'jobs', icon: Briefcase, label: 'Jobs' },
            { id: 'rides', icon: Car, label: 'Rides' },
            { id: 'earnings', icon: Wallet, label: 'Earnings' },
            { id: 'account', icon: User, label: 'Account' },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'home') { /* Stay on page */ }
                  if (tab.id === 'jobs') { /* Jobs shown in DriverOpsPanel */ }
                  if (tab.id === 'rides') { /* Rides shown in DriverRidePanel */ }
                  if (tab.id === 'earnings') navigate('/driver/earnings');
                  if (tab.id === 'account') openProfileModal();
                }}
                className={`flex flex-col items-center gap-1 px-4 py-1 rounded-xl transition-colors ${
                  isActive ? 'text-[#00A86B]' : 'text-gray-400'
                }`}
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {isActive && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#FFD300]" />
                  )}
                </div>
                <span className="text-[10px] font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Driver Profile</h2>
              <button onClick={() => setShowProfileModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Photo Upload */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#00A86B] to-[#008B5A] flex items-center justify-center overflow-hidden border-4 border-white shadow-xl">
                    {profileData.photo_url ? (
                      <img src={profileData.photo_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-12 w-12 text-white" />
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-10 h-10 bg-[#FFD300] rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:scale-105 transition-transform">
                    <Camera className="h-5 w-5 text-gray-800" />
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={profileLoading} />
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-3">Customers see this photo when you're online</p>
              </div>

              {/* Profile Fields */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="h-4 w-4" /> Display Name
                  </label>
                  <input
                    type="text"
                    value={profileData.display_name}
                    onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                    style={{color: "#111827", backgroundColor: "white"}} className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00A86B] focus:border-transparent text-gray-900 dark:text-gray-900"
                    placeholder="How customers will see your name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    style={{color: "#111827", backgroundColor: "white"}} className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00A86B] focus:border-transparent text-gray-900 dark:text-gray-900"
                    placeholder="Your contact number"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Car className="h-4 w-4" /> Vehicle Type
                    </label>
                    <select
                      value={profileData.vehicle_type}
                      onChange={(e) => setProfileData({ ...profileData, vehicle_type: e.target.value })}
                      style={{color: "#111827", backgroundColor: "white"}} className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00A86B] focus:border-transparent text-gray-900 dark:text-gray-900"
                    >
                      <option value="">Select</option>
                      <option value="motorcycle">Motorcycle</option>
                      <option value="car">Car</option>
                      <option value="van">Van</option>
                      <option value="truck">Truck</option>
                      <option value="bicycle">Bicycle</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Plate #</label>
                    <input
                      type="text"
                      value={profileData.vehicle_plate}
                      onChange={(e) => setProfileData({ ...profileData, vehicle_plate: e.target.value.toUpperCase() })}
                      style={{color: "#111827", backgroundColor: "white"}} className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00A86B] focus:border-transparent text-gray-900 dark:text-gray-900"
                      placeholder="ABC 1234"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Bio (Optional)</label>
                  <textarea
                    value={profileData.bio}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    style={{color: "#111827", backgroundColor: "white"}} className="w-full mt-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00A86B] focus:border-transparent resize-none text-gray-900 dark:text-gray-900"
                    rows={3}
                    placeholder="Tell customers about yourself..."
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-400 mt-1">{profileData.bio?.length || 0}/200</p>
                </div>
              </div>

              {/* Vehicle Photos & Requirements */}
              <div 
                onClick={() => { setShowProfileModal(false); navigate('/driver/vehicle-photos'); }}
                className="bg-gradient-to-r from-emerald-50 to-amber-50 rounded-xl p-4 border border-emerald-200 cursor-pointer hover:shadow-md transition-all"
                data-testid="vehicle-photos-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Camera className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-gray-700">Vehicle Photos</h3>
                    <p className="text-xs text-gray-500">Upload required vehicle & profile photos</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Driver Stats */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-bold text-gray-700 mb-3">Your Stats</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[#00A86B]">{driver?.total_deliveries || 0}</p>
                    <p className="text-xs text-gray-500">Deliveries</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#FFD300]">{driver?.rating?.toFixed(1) || '5.0'}</p>
                    <p className="text-xs text-gray-500">Rating</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{driver?.experience_months || 0}m</p>
                    <p className="text-xs text-gray-500">Experience</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={saveProfile}
                disabled={profileLoading}
                className="w-full py-4 bg-gradient-to-r from-[#00A86B] to-[#008B5A] text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow disabled:opacity-50"
              >
                {profileLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    Save Profile
                  </>
                )}
              </button>


              {/* Bank Details Button */}
              <button
                onClick={() => { setShowProfileModal(false); setShowBankModal(true); }}
                className="w-full py-3 bg-emerald-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors mb-3"
              >
                <Building2 className="h-5 w-5" />
                Manage Bank Details
              </button>

              {/* Test Notification Sound Button */}
              <button
                onClick={() => {
                  const audio = new Audio("/sounds/chain.wav");
                  audio.volume = 0.7;
                  audio.play().then(() => {
                    toast.success("Chain sound playing! 🔗");
                  }).catch(() => {
                    toast.error("Could not play sound. Check your device volume.");
                  });
                }}
                className="w-full py-3 bg-yellow-500 text-gray-900 font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-yellow-400 transition-colors"
              >
                <Volume2 className="h-5 w-5" />
                Test Notification Sound
              </button>

              {/* Notification Preferences Button */}
              <button
                onClick={() => { setShowProfileModal(false); setShowPreferencesModal(true); }}
                className="w-full py-3 bg-emerald-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors"
              >
                <MapPin className="h-5 w-5" />
                Notification Preferences
              </button>

              {/* Back to Dashboard Link */}
              <button
                onClick={() => { setShowProfileModal(false); navigate('/dashboard'); }}
                className="w-full py-3 text-gray-600 font-medium hover:text-[#00A86B] transition-colors"
              >
                Go to Main Dashboard →
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Bank Details Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-950 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-950 px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Bank Details</h2>
              <button onClick={() => setShowBankModal(false)} className="p-2 hover:bg-slate-800 rounded-full transition-colors">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <div className="p-4">
              <BankDetailsForm onSaved={() => setShowBankModal(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Notification Preferences Modal */}
      {showPreferencesModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-950 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-950 px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Notification Preferences</h2>
              <button onClick={() => setShowPreferencesModal(false)} className="p-2 hover:bg-slate-800 rounded-full transition-colors">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Parish Selection */}
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-emerald-400" />
                  Preferred Parishes
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Select parishes where you want to receive job notifications. Leave empty to receive all jobs.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {JAMAICA_PARISHES.map(parish => (
                    <button
                      key={parish}
                      onClick={() => toggleParish(parish)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        (preferences.preferred_parishes || []).includes(parish)
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {parish}
                    </button>
                  ))}
                </div>
              </div>

              {/* Package Types */}
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Settings className="h-5 w-5 text-yellow-400" />
                  Package Types
                </h3>
                <div className="space-y-3">
                  {/* Bulky - only if van/truck */}
                  {(driver?.vehicle_type?.toLowerCase() === 'van' || driver?.vehicle_type?.toLowerCase() === 'truck') && (
                    <label className="flex items-center justify-between p-3 bg-slate-800 rounded-xl cursor-pointer">
                      <div>
                        <span className="text-white font-medium">Bulky Packages 🚚</span>
                        <p className="text-slate-400 text-sm">Large items requiring van/truck</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.accepts_bulky || false}
                        onChange={(e) => setPreferences({...preferences, accepts_bulky: e.target.checked})}
                        className="w-5 h-5 accent-emerald-500"
                      />
                    </label>
                  )}
                  <label className="flex items-center justify-between p-3 bg-slate-800 rounded-xl cursor-pointer">
                    <div>
                      <span className="text-white font-medium">Fragile Items 📦</span>
                      <p className="text-slate-400 text-sm">Delicate packages requiring care</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.accepts_fragile !== false}
                      onChange={(e) => setPreferences({...preferences, accepts_fragile: e.target.checked})}
                      className="w-5 h-5 accent-emerald-500"
                    />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-slate-800 rounded-xl cursor-pointer">
                    <div>
                      <span className="text-white font-medium">Food Delivery 🍽️</span>
                      <p className="text-slate-400 text-sm">Prepared food and groceries</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.accepts_food !== false}
                      onChange={(e) => setPreferences({...preferences, accepts_food: e.target.checked})}
                      className="w-5 h-5 accent-emerald-500"
                    />
                  </label>
                </div>
              </div>

              {/* Notification Sound Selection */}
              <div>
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Volume2 className="h-5 w-5 text-purple-400" />
                  Notification Sound
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Choose your preferred notification sound. Tap to preview.
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                  {NOTIFICATION_SOUNDS.map(sound => (
                    <button
                      key={sound.id}
                      onClick={() => selectSound(sound.id)}
                      className={`p-3 rounded-xl text-left transition-all flex items-center justify-between ${
                        (preferences.notification_sound || 'chain') === sound.id
                          ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <div>
                        <span className="font-medium">{sound.name}</span>
                        <p className="text-xs opacity-70">{sound.desc}</p>
                      </div>
                      {playingSound === sound.id ? (
                        <span className="text-xs animate-pulse">🔊 Playing...</span>
                      ) : (preferences.notification_sound || 'chain') === sound.id ? (
                        <span className="text-xs">✓ Selected</span>
                      ) : (
                        <span className="text-xs opacity-50">Tap to play</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSavePreferences}
                disabled={prefLoading}
                className="w-full py-4 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-500 transition-colors disabled:opacity-50"
              >
                {prefLoading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={showQRScanner}
        onClose={() => { setShowQRScanner(false); setScanningOrderId(null); }}
        onScanSuccess={handleScanSuccess}
        orderId={scanningOrderId}
      />
      
      {/* Profile Photo Cropper */}
      <ImageCropper
        isOpen={showCropper}
        onClose={() => { setShowCropper(false); setPendingPhotoFile(null); }}
        onCropComplete={handleCroppedPhoto}
        aspectRatio={1}
        imageType="profile"
        initialFile={pendingPhotoFile}
      />
    </div>
  );
};

// Wrap with error boundary
const DriverHomeWithErrorBoundary = () => (
  <DriverHomeErrorBoundary>
    <DriverHome />
  </DriverHomeErrorBoundary>
);

export default DriverHomeWithErrorBoundary;
